import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Mail, Shield, Users, Heart, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import getCanonicalUrl from '../utils/canonical';

const Napoli = () => {

  return (
    <div className="page-container">
      <Helmet>
        <title>Agenzia Matrimoniale seria a Napoli | Officine del Cuore</title>
        <meta name="description" content="Agenzia Matrimoniale e di Incontri selezionati a Napoli. Officine del Cuore: oltre 18 anni di esperienza, massima riservatezza, iscritti verificati. Contattaci ora." />
        <link rel="canonical" href={getCanonicalUrl('agenzia-matrimoniale-napoli')} />
      </Helmet>

      {/* Hero Section */}
      <section className="page-hero" style={{backgroundImage: 'url(https://customer-assets.emergentagent.com/job_officine-cuore/artifacts/imiimlld_Lungomare%20Napoli.webp)'}}>
        <div className="hero-overlay" />
        <div className="hero-content">
          <h1 className="page-title fade-in">Agenzia Matrimoniale Napoli</h1>
          <p className="page-subtitle fade-in-delay">
            Agenzia Matrimoniale e di Incontri selezionati a Napoli Officine del Cuore
          </p>
        </div>
      </section>

      {/* Intro Section */}
      <section className="section-elegant">
        <div className="max-w-4xl mx-auto px-4">
          <div className="prose-elegant">
            <p>
              Se stai cercando un'agenzia matrimoniale a Napoli seria, discreta e realmente orientata alla creazione di relazioni durature, Officine del Cuore è il punto di riferimento per uomini e donne che desiderano incontrare una persona compatibile con cui costruire qualcosa di autentico. Da oltre 19 anni operiamo nel settore delle relazioni, aiutando single motivati a trovare non semplici incontri, ma legami concreti basati su valori condivisi, affinità caratteriale e obiettivi di vita.
            </p>
          </div>
        </div>
      </section>

      {/* Different Approach Section */}
      <section className="section-elegant bg-warm-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="section-title text-center mb-8">
            Un modo diverso di incontrarsi
          </h2>
          <div className="prose-elegant">
            <p>
              A differenza delle app di incontri dove non esiste riservatezza, selezione e ascolto, il nostro metodo si basa su una selezione attenta e personalizzata. Ogni persona viene conosciuta attraverso un colloquio diretto, che ci permette di comprendere davvero chi sei e cosa stai cercando, viene poi verificata e certificata. Questo ci consente di proporti incontri mirati e sicuri, con persone che condividono il tuo stesso desiderio: costruire una relazione seria.
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section-elegant">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="section-title text-center mb-12">
            Perché scegliere Officine del Cuore Napoli
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-gold flex-shrink-0 mt-1" />
              <p className="text-burgundy/90">Esperienza consolidata nel settore delle relazioni</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-gold flex-shrink-0 mt-1" />
              <p className="text-burgundy/90">Approccio umano e personalizzato</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-gold flex-shrink-0 mt-1" />
              <p className="text-burgundy/90">Massima riservatezza</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-gold flex-shrink-0 mt-1" />
              <p className="text-burgundy/90">Selezione accurata dei profili</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-gold flex-shrink-0 mt-1" />
              <p className="text-burgundy/90">Iscritti verificati e certificati</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-gold flex-shrink-0 mt-1" />
              <p className="text-burgundy/90">Supporto costante durante tutto il percorso</p>
            </div>
          </div>
          <p className="text-center mt-8 text-lg font-medium text-burgundy">
            Non si tratta di quantità, ma di qualità degli incontri.
          </p>
        </div>
      </section>

      {/* Target Audience Section */}
      <section className="section-elegant bg-warm-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="section-title text-center mb-8">
            A chi ci rivolgiamo
          </h2>
          <div className="prose-elegant">
            <p className="mb-4">Il nostro servizio è dedicato a:</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-gold flex-shrink-0 mt-1" />
                <span>uomini e donne single</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-gold flex-shrink-0 mt-1" />
                <span>persone divorziate, vedove, separate legalmente</span>
              </li>
            </ul>
            <p className="mt-6 font-medium">
              Se desideri una relazione autentica, sei nel posto giusto.
            </p>
          </div>
        </div>
      </section>

      {/* Tailored Meetings Section */}
      <section className="section-elegant">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="section-title text-center mb-8">
            Incontri mirati e selezionati
          </h2>
          <div className="prose-elegant">
            <p className="mb-4">Ogni proposta viene valutata con attenzione, tenendo conto di:</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
              <Card className="benefit-card">
                <CardContent className="p-6 text-center">
                  <h3 className="benefit-title">Compatibilità caratteriale</h3>
                </CardContent>
              </Card>
              <Card className="benefit-card">
                <CardContent className="p-6 text-center">
                  <h3 className="benefit-title">Stile di vita</h3>
                </CardContent>
              </Card>
              <Card className="benefit-card">
                <CardContent className="p-6 text-center">
                  <h3 className="benefit-title">Aspettative relazionali e preferenze</h3>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Coverage Area Section */}
      <section className="section-elegant bg-warm-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="section-title text-center mb-8">
            Dalla sede di Napoli operiamo anche su tutta la Campania
          </h2>
          <div className="prose-elegant text-center">
            <p>
              Pur avendo sede a Napoli, Officine del Cuore segue attivamente clienti su tutta la Campania offrendo un servizio organizzato in base alle esigenze di ogni singolo cliente.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Napoli */}
      <section className="section-elegant bg-warm-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="section-title text-center mb-12">
            Contattaci
          </h2>
          <Card className="contact-card">
            <CardContent className="p-8">
              <div className="contact-info-list">
                <div className="contact-info-item">
                  <MapPin className="text-gold" size={24} />
                  <div>
                    <p className="font-medium">Napoli e Caserta</p>
                    <p className="text-sm">Via G. Testa, 16 - Napoli (NA)</p>
                  </div>
                </div>
                <div className="contact-info-item">
                  <Phone className="text-gold" size={24} />
                  <a href="tel:+393292626152" className="hover:text-gold transition-colors">
                    +39 3292626152
                  </a>
                </div>
                <div className="contact-info-item">
                  <Mail className="text-gold" size={24} />
                  <a href="mailto:info@officinedelcuore.it" className="hover:text-gold transition-colors">
                    info@officinedelcuore.it
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section-alt">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="cta-title">
            Richiedi un Colloquio Riservato
          </h2>
          <p className="cta-description">
            Contattaci per un colloquio conoscitivo gratuito. 
            Scopri come possiamo aiutarti a trovare la persona giusta per te a Napoli.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-6">
            <Link to="/contatti">
              <Button size="lg" className="btn-primary btn-large">
                Contattaci Ora
              </Button>
            </Link>
            <a href="tel:+393292626152">
              <Button size="lg" className="btn-primary btn-large">
                Chiama Ora
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Napoli;
